const chapter = {
  id: 'dual-nature',
  slug: 'dual-nature',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Dual Nature of Radiation & Matter',
        hindi: 'विकिरण और द्रव्य की द्वैत प्रकृति',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Optics & Modern', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'photoelectric',
      title: {
        english: 'Photoelectric Effect',
        hindi: 'प्रकाश-विद्युत प्रभाव',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'When light of suitable frequency is incident on a metal surface, electrons are emitted. Discovered by Hertz (1887), studied quantitatively by Lenard and Millikan; explained by Einstein (1905, Nobel 1921).',
        hindi: 'जब उपयुक्त आवृत्ति का प्रकाश किसी धातु की सतह पर आपतित होता है, तो इलेक्ट्रॉन उत्सर्जित होते हैं। हर्ट्ज़ (1887) द्वारा खोजा गया, लेनार्ड और मिलिकन द्वारा मात्रात्मक रूप से अध्ययन किया गया; आइंस्टीन (1905, नोबेल 1921) द्वारा समझाया गया।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Threshold frequency** $\\nu_0$ — below this, no emission, no matter how intense.',
        hindi: '**सीमा आवृत्ति** $\\nu_0$ - इसके नीचे, कोई उत्सर्जन नहीं, चाहे कितना भी तीव्र क्यों न हो।',
            },
            {
              english: '**Work function** $\\phi_0 = h\\nu_0$ — minimum energy to liberate an electron.',
        hindi: '**कार्य फलन** $\\phi_0 = h\\nu_0$ — एक इलेक्ट्रॉन को मुक्त करने के लिए न्यूनतम ऊर्जा।',
            },
            {
              english: 'Photoelectric current ∝ intensity (for $\\nu > \\nu_0$); but $K_{\\max}$ is **independent of intensity**.',
        hindi: 'फोटोइलेक्ट्रिक करंट ∝ तीव्रता ($\\nu > \\nu_0$ के लिए); लेकिन $K_{\\max}$ **तीव्रता से स्वतंत्र** है।',
            },
            {
              english: '$K_{\\max}$ depends linearly on frequency, not on intensity.',
        hindi: '$K_{\\max}$ रैखिक रूप से आवृत्ति पर निर्भर करता है, तीव्रता पर नहीं।',
            },
            {
              english: 'Effect is **instantaneous** ($< 10^{-9}$ s).',
        hindi: 'प्रभाव **तात्कालिक** ($< 10^{-9}$ s) है। **रोकने की क्षमता** $< 10^{-9}$ - न्यूनतम रिवर्स वोल्टेज जो सबसे तेज़ फोटोइलेक्ट्रॉन को रोकता है: ।',
            },
            {
              english: '**Stopping potential** $V_0$ — minimum reverse voltage that stops fastest photoelectrons: $eV_0 = K_{\\max}$.',
        hindi: '**रोकने की क्षमता** $V_0$ - न्यूनतम रिवर्स वोल्टेज जो सबसे तेज़ फोटोइलेक्ट्रॉन को रोकता है: $eV_0 = K_{\\max}$।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Einstein\'s photoelectric equation',
        hindi: 'आइंस्टीन का फोटोइलेक्ट्रिक समीकरण',
          },
          statement: {
            english: `$$h\\nu = \\phi_0 + K_{\\max} = h\\nu_0 + \\tfrac12 m v_{\\max}^2.$$
Equivalently $eV_0 = h(\\nu - \\nu_0)$. Plot of $V_0$ vs $\\nu$ is a straight line with slope $h/e$ and $x$-intercept $\\nu_0$ — common in Millikan's experiment.`,
            hindi: '$$h\\nu = \\phi_0 + K_{\\max} = h\\nu_0 + \\tfrac12 m v_{\\max}^2.$$ समान रूप से $eV_0 = h(\\nu - \\nu_0)$. $V_0$ बनाम $\\nu$ का प्लॉट ढलान $h/e$ और $x$-इंटरसेप्ट $\\nu_0$ के साथ एक सीधी रेखा है - मिलिकन के प्रयोग में आम है।',
          },
        },
        {
          type: 'note',
          text: {
            english: 'Wave theory predicts emission for any frequency given enough intensity / time — contradicted by experiments. Photon (quantum) picture resolves this.',
        hindi: 'तरंग सिद्धांत पर्याप्त तीव्रता/समय दिए जाने पर किसी भी आवृत्ति के लिए उत्सर्जन की भविष्यवाणी करता है - प्रयोगों द्वारा इसका खंडन किया गया है। फोटॉन (क्वांटम) चित्र इसका समाधान करता है।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Light of $\\lambda = 300$ nm strikes a metal of work function $2$ eV. Find $K_{\\max}$ and $V_0$.',
        hindi: '$\\lambda = 300$ nm का प्रकाश कार्य फलन $2$ eV की धातु से टकराता है। $K_{\\max}$ और $V_0$ खोजें।',
          },
          solution: {
            english: '$E_{\\text{photon}} = 1240/300 = 4.13$ eV. $K_{\\max} = 4.13 - 2 = 2.13$ eV. $V_0 = 2.13$ V.',
        hindi: '$E_{\\text{photon}} = 1240/300 = 4.13$ eV. $K_{\\max} = 4.13 - 2 = 2.13$ eV. $V_0 = 2.13$ वी.',
          },
        },
      ],
    },
    {
      id: 'photon',
      title: {
        english: 'Photon — Particle Nature of Light',
        hindi: 'फोटॉन — प्रकाश की कण प्रकृति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Each photon carries energy $E = h\\nu = hc/\\lambda$ and momentum $p = E/c = h/\\lambda$.',
        hindi: 'प्रत्येक फोटॉन ऊर्जा $E = h\\nu = hc/\\lambda$ और संवेग $p = E/c = h/\\lambda$ वहन करता है।',
            },
            {
              english: 'Photons are uncharged, travel with $c$ in vacuum, mass-less (rest mass = 0).',
        hindi: 'फोटॉन अनावेशित होते हैं, निर्वात में $c$ के साथ यात्रा करते हैं, द्रव्यमान-रहित (बाकी mass = 0)।',
            },
            {
              english: 'In any interaction, photons are exchanged in **quantised** units.',
        hindi: 'किसी भी इंटरैक्शन में, फोटॉनों का आदान-प्रदान **मात्राबद्ध** इकाइयों में होता है।',
            },
            {
              english: 'Number of photons emitted per second by source of power $P$: $N = P/(h\\nu)$.',
        hindi: 'शक्ति स्रोत $P$: $N = P/(h\\nu)$ द्वारा प्रति सेकंड उत्सर्जित फोटॉन की संख्या।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Useful: $hc = 1240$ eV·nm. So photon energy (eV) $= 1240/\\lambda(\\text{nm})$.',
        hindi: 'उपयोगी: $hc = 1240$ eV·nm. तो फोटॉन ऊर्जा (eV) $= 1240/\\lambda(\\text{nm})$।',
          },
        },
      ],
    },
    {
      id: 'matter-waves',
      title: {
        english: 'de Broglie Hypothesis & Matter Waves',
        hindi: 'द ब्रॉग्ली परिकल्पना और द्रव्य तरंगें',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'de Broglie wavelength',
        hindi: 'डी ब्रोगली तरंगदैर्घ्य',
          },
          statement: {
            english: `$$\\lambda = \\dfrac{h}{p} = \\dfrac{h}{mv} = \\dfrac{h}{\\sqrt{2mK}}.$$
For a charged particle accelerated through potential $V$: $\\lambda = h/\\sqrt{2mqV}$.`,
            hindi: '$$\\lambda = \\dfrac{h}{p} = \\dfrac{h}{mv} = \\dfrac{h}{\\sqrt{2mK}}.$$ संभावित $V$: $\\lambda = h/\\sqrt{2mqV}$ के माध्यम से त्वरित किए गए आवेशित कण के लिए।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'For an **electron** accelerated through $V$ volts: $\\lambda \\approx 12.27/\\sqrt{V}$ Å.',
        hindi: '$V$ वोल्ट के माध्यम से त्वरित **इलेक्ट्रॉन** के लिए: $\\lambda \\approx 12.27/\\sqrt{V}$ Å।',
            },
            {
              english: 'Heavier particles ⇒ smaller $\\lambda$, hence wave nature unobservable for macroscopic bodies.',
        hindi: 'भारी कण ⇒ छोटे $\\lambda$, इसलिए तरंग प्रकृति स्थूल पिंडों के लिए अप्राप्य है।',
            },
            {
              english: '**Davisson–Germer experiment** (1927): diffraction of electrons from Ni crystal confirmed matter waves.',
        hindi: '**डेविसन-जर्मर प्रयोग** (1927): नी क्रिस्टल से इलेक्ट्रॉनों का विवर्तन पदार्थ तरंगों की पुष्टि करता है।',
            },
            {
              english: 'For thermal neutrons at temperature $T$: $\\lambda = h/\\sqrt{3 m k_B T}$.',
        hindi: '$T$: $\\lambda = h/\\sqrt{3 m k_B T}$ तापमान पर थर्मल न्यूट्रॉन के लिए।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'de Broglie wavelength of an electron accelerated through 100 V?',
        hindi: '100 V के माध्यम से त्वरित एक इलेक्ट्रॉन की डी ब्रोगली तरंग दैर्ध्य?',
          },
          solution: {
            english: '$\\lambda = 12.27/\\sqrt{100} = 1.227$ Å.',
        hindi: '$\\lambda = 12.27/\\sqrt{100} = 1.227$ Å.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'h\\nu = \\phi_0 + K_{\\max}',
      desc: {
        english: 'Einstein equation',
        hindi: 'आइंस्टीन समीकरण',
      },
    },
    {
      latex: 'eV_0 = h(\\nu - \\nu_0)',
      desc: {
        english: 'Stopping potential',
        hindi: 'रुकने की संभावना',
      },
    },
    {
      latex: 'E = h\\nu = hc/\\lambda',
      desc: {
        english: 'Photon energy',
        hindi: 'फोटॉन ऊर्जा',
      },
    },
    {
      latex: 'p = h/\\lambda',
      desc: {
        english: 'Photon momentum',
        hindi: 'फोटॉन गति',
      },
    },
    {
      latex: '\\lambda = h/\\sqrt{2mK}',
      desc: {
        english: 'de Broglie',
        hindi: 'डी ब्रोगली',
      },
    },
    {
      latex: '\\lambda_e = 12.27/\\sqrt{V}\\ \\text{Å}',
      desc: {
        english: 'Electron, accel. through $V$ volts',
        hindi: 'इलेक्ट्रॉन, एक्सेल। $V$ वोल्ट के माध्यम से',
      },
    },
    {
      latex: 'N = P/(h\\nu)',
      desc: {
        english: 'Photons per second',
        hindi: 'फोटॉन प्रति सेकंड',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Photoelectric',
      problem: {
        english: 'If frequency of incident light is doubled (still > $\\nu_0$), $K_{\\max}$ becomes:',
        hindi: 'यदि आपतित प्रकाश की आवृत्ति दोगुनी कर दी जाए (फिर भी > $\\nu_0$), $K_{\\max}$ हो जाती है:',
      },
      answer: {
        english: '$2h\\nu - \\phi_0$, i.e. more than doubles',
        hindi: '$2h\\nu - \\phi_0$, यानि दोगुने से भी ज्यादा',
      },
      solution: {
        english: '$K_{\\max} = h\\nu - \\phi_0$. Doubling $\\nu$: $K\'_{\\max} = 2h\\nu - \\phi_0 > 2K_{\\max}$.',
        hindi: '$K_{\\max} = h\\nu - \\phi_0$. दोहरीकरण $\\nu$: $K\'_{\\max} = 2h\\nu - \\phi_0 > 2K_{\\max}$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'de Broglie',
      problem: {
        english: 'Ratio of de Broglie wavelengths of an electron and a proton with same KE?',
        hindi: 'समान KE वाले एक इलेक्ट्रॉन और एक प्रोटॉन की डी ब्रोगली तरंग दैर्ध्य का अनुपात?',
      },
      answer: {
        english: '$\\sqrt{m_p/m_e} \\approx 42.8$',
        hindi: '$\\sqrt{m_p/m_e} \\approx 42.8$',
      },
      solution: {
        english: '$\\lambda \\propto 1/\\sqrt{m}$ for same $K$.',
        hindi: 'समान $K$ के लिए $\\lambda \\propto 1/\\sqrt{m}$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Stopping potential',
      problem: {
        english: 'Work function 2.5 eV; light of wavelength 400 nm. $V_0$?',
        hindi: 'कार्य फ़ंक्शन 2.5 eV; तरंग दैर्ध्य 400 एनएम का प्रकाश. $V_0$?',
      },
      answer: {
        english: '$0.6$ V',
        hindi: '$0.6$ वी',
      },
      solution: {
        english: '$E = 1240/400 = 3.1$ eV. $V_0 = 3.1 - 2.5 = 0.6$ V.',
        hindi: '$ई = 1240/400 = 3.1$ ईवी। $V_0 = 3.1 - 2.5 = 0.6$ वी.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Photon momentum',
      problem: {
        english: 'Momentum of photon of $\\lambda = 500$ nm?',
        hindi: '$\\lambda = 500$ एनएम के फोटॉन का संवेग?',
      },
      answer: {
        english: '$1.32\\times 10^{-27}$ kg·m/s',
        hindi: '$1.32\\times 10^{-27}$ kg·m/s',
      },
      solution: {
        english: '$p = h/\\lambda = 6.63{\\times}10^{-34}/(5{\\times}10^{-7})$.',
        hindi: '$p = h/\\lambda = 6.63{\\times}10^{-34}/(5{\\times}10^{-7})$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Threshold',
      problem: {
        english: 'Threshold wavelength of metal of work function 4.14 eV?',
        hindi: 'कार्य फलन की धातु की दहलीज तरंगदैर्घ्य 4.14 eV?',
      },
      answer: {
        english: '$300$ nm',
        hindi: '$300$ एनएम',
      },
      solution: {
        english: '$\\lambda_0 = 1240/4.14 = 300$ nm.',
        hindi: '$\\lambda_0 = 1240/4.14 = 300$ एनएम।',
      },
    },
  ],
  next: {
    slug: 'atoms',
    title: 'Atoms',
  },
};

export default chapter;
